import { db } from '@/core/db/dexie'
import { parteDiariaEndpoints } from '@/core/api/endpoints'
import { ApiError } from '@/core/api/fetchClient'
import type { EquipeParteDto, MveParteDto, PacoteOfflineParteDiariaDto, ServicoParteDto } from '@/types/parteDiariaDtos'
import { hojeSP } from './parteDiariaRegras'
import { gravarCache, lerCache } from './parteDiariaCache'

/**
 * Pré-carga da Parte Diária para uso SEM SINAL.
 *
 * Antes (até 0.1.7) o cache só era preenchido quando o operador abria cada tela
 * COM rede: os serviços de um par (MVE, obra) só existiam offline se aquele par
 * exato já tivesse sido aberto online. No campo isso significava lista vazia e
 * lançamento impossível. Agora, sempre que há rede, o app baixa em segundo
 * plano um pacote único (`/parte-diaria/pacote-offline`) e grava nas MESMAS
 * chaves que os hooks já leem — mais a fatoração dos serviços, que permite
 * montar a lista de qualquer par localmente:
 *
 *   serviços(mve, obra) = (subgrupo(mve) ∩ obra) ∪ universais   (= consulta A.10)
 *
 * O servidor continua sendo a autoridade: tudo é revalidado no envio.
 */

const CHAVE_SERVICOS = 'pacote:servicos'
const CHAVE_RESUMO = 'pacote:resumo-dia'

type ResumoPorMve = Record<string, { horimetroSugerido: number | null; formularioMedicao: 'MVE' | 'P' | null; linhasHoje: number }>

interface ResumoDiaCache {
  /** Data (SP) a que o resumo se refere (o "hoje" do servidor quando o pacote foi montado). */
  dt: string
  porMve: ResumoPorMve
  /** O mesmo resumo para o dia anterior — o app lança ontem e hoje. */
  ontem?: { dt: string; porMve: ResumoPorMve }
}

/**
 * O que o app sabe sobre o dia de uma MVE SEM falar com o servidor:
 *  - resumo do pacote (todas as MVEs do chefe, não só as de contexto em cache);
 *  - lançamentos desta MVE que ainda estão no outbox deste aparelho hoje — o
 *    SIGA ainda não os conhece, mas a próxima linha tem que partir deles.
 */
export interface ApoioOffline {
  horimetroSugerido: number | null
  formularioMedicao: 'MVE' | 'P' | null
  /** Linhas de hoje já no SIGA segundo o resumo (0 quando o resumo é de outro dia). */
  linhasHoje: number
  /** Data do resumo usado (null = sem resumo para esta MVE). */
  referencia: string | null
  /** Linhas de hoje desta MVE paradas no outbox local. */
  pendentesHoje: number
  ultimoTerminoPendente: number | null
}

export async function carregarApoioOffline(mveId: number, dt: string): Promise<ApoioOffline | null> {
  const cache = (await lerCache<ResumoDiaCache>(CHAVE_RESUMO))?.value ?? null
  // Resumo da data pedida (exato); senão o mais novo ANTERIOR a ela (o último
  // término conhecido ainda vale como início). Resumo de data POSTERIOR não
  // serve: pode incluir linhas lançadas depois do dia que se quer lançar.
  const candidatos = [cache ? { dt: cache.dt, porMve: cache.porMve } : null, cache?.ontem ?? null]
    .filter((c): c is { dt: string; porMve: ResumoPorMve } => c != null && c.dt <= dt)
    .sort((a, b) => b.dt.localeCompare(a.dt))
  const resumo = candidatos[0] ?? null
  const r = resumo?.porMve[String(mveId)] ?? null
  let pendentesHoje = 0
  let ultimoTerminoPendente: number | null = null
  try {
    const fila = await db.out_parte_diaria.where('mveId').equals(mveId).toArray()
    for (const item of fila) {
      // Pendente de dia ANTERIOR também empurra o início (lancei ontem offline, agora lanço hoje).
      if (item.modulo !== 'MVE' || item.dt > dt || !item.mve) continue
      if (item.syncStatus !== 'PENDING' && item.syncStatus !== 'SYNCING' && item.syncStatus !== 'FAILED') continue
      if (item.dt === dt) pendentesHoje += item.mve.linhas.length
      for (const l of item.mve.linhas) {
        if (l.medicaoTermino != null && (ultimoTerminoPendente == null || l.medicaoTermino > ultimoTerminoPendente)) {
          ultimoTerminoPendente = l.medicaoTermino
        }
      }
    }
  } catch {
    /* outbox indisponível — segue só com o resumo */
  }
  if (!r && pendentesHoje === 0) return null
  return {
    horimetroSugerido: r?.horimetroSugerido ?? null,
    formularioMedicao: r?.formularioMedicao ?? null,
    // Resumo de ontem: o último término continua valendo como início de hoje,
    // mas as "linhas de hoje" dele eram de ontem.
    linhasHoje: r && resumo?.dt === dt ? r.linhasHoje : 0,
    referencia: r ? (resumo?.dt ?? null) : null,
    pendentesHoje,
    ultimoTerminoPendente,
  }
}

interface PacoteServicos {
  servicos: ServicoParteDto[]
  universais: number[]
  servicosPorSubgrupo: Record<string, number[]>
  servicosPorObra: Record<string, number[]>
  subgrupoPorMve: Record<string, number | null>
}

export type ResultadoPacote =
  | { status: 'ok'; mves: MveParteDto[]; obras: number }
  | { status: 'sem_acesso'; message: string }
  | { status: 'falha'; message: string }

export async function baixarPacoteParteDiaria(): Promise<ResultadoPacote> {
  const hoje = hojeSP()
  let pacote: PacoteOfflineParteDiariaDto
  try {
    pacote = await parteDiariaEndpoints.pacoteOffline(hoje)
  } catch (err) {
    // 403/422 = perfil sem acesso ao módulo (não é chefe/suporte/admin): não é falha.
    if (err instanceof ApiError && (err.status === 403 || err.status === 422)) {
      return { status: 'sem_acesso', message: err.message }
    }
    return { status: 'falha', message: err instanceof Error ? err.message : String(err) }
  }

  const agora = Date.now()
  const equipesDaObra = (projetoId: number): EquipeParteDto[] => pacote.equipesPorProjeto[String(projetoId)] ?? []
  const servicos: PacoteServicos = {
    servicos: pacote.servicos,
    universais: pacote.universais,
    servicosPorSubgrupo: pacote.servicosPorSubgrupo,
    servicosPorObra: pacote.servicosPorObra,
    subgrupoPorMve: pacote.subgrupoPorMve,
  }
  try {
    await db.cache_parte_diaria.bulkPut([
      { key: `mves:${pacote.mes}`, value: pacote.mves, fetchedAt: agora },
      { key: 'obras', value: pacote.obras, fetchedAt: agora },
      { key: CHAVE_SERVICOS, value: servicos, fetchedAt: agora },
      ...(pacote.resumoDia
        ? [{ key: CHAVE_RESUMO, value: { dt: pacote.dt, porMve: pacote.resumoDia, ontem: pacote.resumoOntem } satisfies ResumoDiaCache, fetchedAt: agora }]
        : []),
      ...pacote.obras.map((o) => ({ key: `equipes:${o.id}`, value: equipesDaObra(o.projetoId), fetchedAt: agora })),
    ])
  } catch (e) {
    return { status: 'falha', message: `Falha ao gravar o pacote offline: ${e instanceof Error ? e.message : String(e)}` }
  }
  return { status: 'ok', mves: pacote.mves, obras: pacote.obras.length }
}

/**
 * Serviços de (MVE, obra) montados a partir do pacote. `null` = não há pacote
 * ou a MVE não fazia parte dele (não dá para afirmar nada sobre a lista).
 */
export async function servicosDoPacote(mveId: number, obraId: number): Promise<ServicoParteDto[] | null> {
  const row = await lerCache<PacoteServicos>(CHAVE_SERVICOS)
  if (!row) return null
  const p = row.value
  if (!(String(mveId) in p.subgrupoPorMve)) return null
  const subgrupo = p.subgrupoPorMve[String(mveId)]
  const doSubgrupo = new Set(subgrupo != null ? (p.servicosPorSubgrupo[String(subgrupo)] ?? []) : [])
  const ids = new Set<number>(p.universais)
  for (const id of p.servicosPorObra[String(obraId)] ?? []) if (doSubgrupo.has(id)) ids.add(id)
  return p.servicos.filter((s) => ids.has(s.id)).sort((a, b) => a.nome.localeCompare(b.nome))
}

// ---------------------------------------------------------------------------
//  Contexto do dia (horímetro inicial, bloqueios, modo) das MVEs em uso
// ---------------------------------------------------------------------------

const MAX_CONTEXTOS = 80
const CONTEXTO_VALE_MS = 60 * 60 * 1000
const PARALELO = 3

/**
 * Baixa o contexto de HOJE das MVEs que o chefe realmente está usando (com
 * lançamento no mês e medição ainda não gerada, mais as que têm rascunho).
 * É o que dá o horímetro inicial e o "dia novo / frações" sem rede. Limitado a
 * 80 MVEs (as de mais lançamentos primeiro) e reaproveita o que tem menos de 1 h.
 */
export async function aquecerContextos(mves: MveParteDto[]): Promise<{ baixados: number; total: number }> {
  const hoje = hojeSP()
  // Contextos de outros dias não servem mais (a chave leva a data) — limpa.
  try {
    await db.cache_parte_diaria
      .where('key')
      .startsWith('contexto:')
      .filter((r) => !r.key.endsWith(`:${hoje}`))
      .delete()
  } catch {
    /* limpeza é best-effort */
  }

  const comRascunho = new Set<number>()
  try {
    for (const d of await db.draft_parte_diaria.toArray()) if (d.mveId != null) comRascunho.add(d.mveId)
  } catch {
    /* sem rascunhos */
  }
  const alvo = mves
    .filter((m) => !m.medicaoGerada && (m.qtdMovimentacao > 0 || comRascunho.has(m.id)))
    .sort((a, b) => Number(comRascunho.has(b.id)) - Number(comRascunho.has(a.id)) || b.qtdMovimentacao - a.qtdMovimentacao)
    .slice(0, MAX_CONTEXTOS)

  let baixados = 0
  let semRede = false
  const fila = [...alvo]
  async function trabalhador() {
    for (;;) {
      const m = fila.shift()
      if (!m || semRede) return
      const key = `contexto:${m.id}:${hoje}`
      const atual = await lerCache<unknown>(key)
      if (atual && Date.now() - atual.fetchedAt < CONTEXTO_VALE_MS) continue
      try {
        const ctx = await parteDiariaEndpoints.contexto(m.id, hoje)
        await gravarCache(key, ctx)
        baixados += 1
      } catch (err) {
        // Sem rede: para tudo. Recusa de negócio (4xx) de UMA MVE: segue para a próxima.
        if (!(err instanceof ApiError) || err.isOffline || err.status === 401) semRede = true
      }
    }
  }
  await Promise.all(Array.from({ length: PARALELO }, trabalhador))
  return { baixados, total: alvo.length }
}
